import { KnownSpeciesCard } from "@/app/components/KnownSpeciesCard";
import { ScreenHead } from "@/app/components/ScreenHead";
import { Button } from "@/app/components/system/Button";
import { XIcon } from "@/app/components/system/Icons";
import { searchSpecies, selectSearchedSpecies } from "@/app/lib/client";
import { useSpecies } from "@/app/lib/data-context";
import { PLANT_PALETTE, hashStr } from "@/palette";
import type { Species } from "@/types";
import { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

interface SpeciesSearchResult {
  scientific: string;
  common: string | null;
}

// Backend requires at least 3 characters (see /api/assistants/species-explorer).
const MIN_QUERY_LENGTH = 3;

async function searchSpeciesByName(q: string): Promise<SpeciesSearchResult[]> {
  const { results } = await searchSpecies(q);
  return results;
}

export function SpeciesSearchScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultPotId = searchParams.get("potId") ?? undefined;
  const { species } = useSpecies();

  const inputRef = useRef<HTMLInputElement | null>(null);
  // Monotonic tokens so a late search/pick response can be ignored if the user
  // has since cleared the field or started something newer.
  const searchSeqRef = useRef(0);
  const pickSeqRef = useRef(0);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SpeciesSearchResult[]>([]);
  // The trimmed query that produced `results` — results/empty-state only show
  // while the live query still matches it, so editing hides stale matches.
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [pickingKey, setPickingKey] = useState<string | null>(null);

  // Invalidate any in-flight search/pick and reset to the initial state.
  const clearSearch = () => {
    searchSeqRef.current++;
    pickSeqRef.current++;
    setQuery("");
    setResults([]);
    setSearchedQuery(null);
    setError(null);
    setHasSearched(false);
    setLoading(false);
    setPickingKey(null);
    inputRef.current?.focus();
  };

  const known = useMemo(() => (species ?? []).slice().sort((a, b) => {
    const ai = a.lastSeenAt || a.firstSeenAt || "";
    const bi = b.lastSeenAt || b.firstSeenAt || "";
    return bi.localeCompare(ai);
  }), [species]);

  const trimmed = query.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_QUERY_LENGTH;
  const canSubmit = trimmed.length >= MIN_QUERY_LENGTH && !loading && !pickingKey;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const seq = ++searchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const found = await searchSpeciesByName(trimmed);
      if (seq !== searchSeqRef.current) return; // superseded by a clear
      setResults(found);
      setSearchedQuery(trimmed);
      setHasSearched(true);
    } catch (err) {
      if (seq !== searchSeqRef.current) return;
      setError((err as Error).message || "Search failed");
      setResults([]);
      setSearchedQuery(trimmed);
      setHasSearched(true);
    } finally {
      if (seq === searchSeqRef.current) setLoading(false);
    }
  };

  const onPick = async (r: SpeciesSearchResult) => {
    if (pickingKey) return;
    const seq = ++pickSeqRef.current;
    setPickingKey(r.scientific);
    setError(null);
    try {
      const { species } = await selectSearchedSpecies({
        scientific: r.scientific,
        common: r.common || r.scientific,
      });
      if (seq !== pickSeqRef.current) return; // user cleared/abandoned mid-pick
      if (!species) throw new Error("Couldn’t load that species");
      navigate("/scan-result", { state: { result: species, defaultPotId, fromKnown: true, fromManual: false } });
    } catch (err) {
      if (seq !== pickSeqRef.current) return;
      setError((err as Error).message || "Couldn’t add that species");
    } finally {
      if (seq === pickSeqRef.current) setPickingKey(null);
    }
  };

  const onPickKnown = (s: Species) => {
    navigate("/scan-result", { state: { result: s, defaultPotId, fromKnown: true, fromManual: false } });
  };

  const onBack = () => navigate("/add" + (defaultPotId ? `?potId=${defaultPotId}` : ""));

  return (
    <div className="screen pad">
      <ScreenHead title="Search Species" subtitle="Look up a species by name." onBack={onBack} />

      <form onSubmit={onSubmit} className="flex items-center gap-2.5 mb-3">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            className="field-input-bare px-3.5 py-3 pr-10"
            placeholder="Scientific or common name…"
            value={query}
            onChange={(e) => {
              const next = e.target.value;
              setQuery(next);
              setError(null);
              if (next.trim().length === 0) {
                // Emptying the field behaves like clearing: invalidate in-flight work.
                searchSeqRef.current++;
                pickSeqRef.current++;
                setResults([]);
                setSearchedQuery(null);
                setHasSearched(false);
                setLoading(false);
                setPickingKey(null);
              }
            }}
            autoFocus
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={clearSearch}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full text-ink-3 bg-transparent border-0 cursor-pointer hover:text-ink hover:bg-cream-2"
            >
              <XIcon size={14} stroke={2} />
            </button>
          )}
        </div>
        <Button type="submit" variant="primary" disabled={!canSubmit}>
          {loading ? "Searching…" : "Search"}
        </Button>
      </form>

      {!error && tooShort && (
        <p className="font-sans text-[13px] text-ink-3 italic px-1 py-2">
          Type at least {MIN_QUERY_LENGTH} characters to search.
        </p>
      )}

      {error && (
        <p className="font-sans text-[13px] text-ink italic px-1 py-2">{error}</p>
      )}

      {!error && hasSearched && !loading && results.length === 0 && trimmed === searchedQuery && (
        <p className="font-sans text-[13px] text-ink-3 italic px-1 py-2">
          No matches yet — try a different name.
        </p>
      )}

      {loading && (
        <div className="flex flex-col gap-2.5 mt-1" aria-hidden="true">
          {[
            { title: "58%", sub: "34%" },
            { title: "44%", sub: "46%" },
            { title: "66%", sub: "30%" },
          ].map((w, i) => (
            <div key={i} className="flex items-center gap-3.5 p-4.5 bg-paper border border-line rounded-[18px]">
              <span className="skeleton w-13 h-13 rounded-2xl shrink-0" />
              <span className="flex-1 flex flex-col gap-2 min-w-0">
                <span className="skeleton h-3.5 rounded-full" style={{ width: w.title }} />
                <span className="skeleton h-2.5 rounded-full" style={{ width: w.sub }} />
              </span>
            </div>
          ))}
        </div>
      )}

      {!loading && results.length > 0 && trimmed === searchedQuery && (
        <div className={`flex flex-col gap-2.5 mt-1${pickingKey ? " pointer-events-none" : ""}`}>
          {results.map((r) => {
            const title = r.common || r.scientific;
            const isPicking = pickingKey === r.scientific;
            const dimmed = pickingKey && !isPicking;
            return (
              <div key={r.scientific} className={`relative${dimmed ? " opacity-50" : ""}`}>
                <KnownSpeciesCard
                  species={{
                    scientific: r.scientific,
                    common: r.common,
                    color: PLANT_PALETTE[hashStr(r.scientific) % PLANT_PALETTE.length]!,
                    initial: (title[0] || "?").toUpperCase(),
                    labelImageUrl: null,
                  }}
                  onClick={() => onPick(r)}
                />
                {isPicking && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 font-sans text-[12.5px] text-ink-3 italic bg-paper pl-2">
                    Adding…
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!hasSearched && !loading && known.length > 0 && (
        <section className="mt-4.5">
          <h3 className="font-sans text-[13px] font-medium text-ink-2 m-0 mb-2.5 uppercase tracking-[0.04em]">
            Plants you've seen before
          </h3>
          <div className="flex flex-col gap-2.5">
            {known.map((s) => (
              <KnownSpeciesCard key={s.id} species={s} onClick={() => onPickKnown(s)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
